import { c as _c } from "react/compiler-runtime";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { getRoleMenuItemSx, RoleMenuItem } from "@components/role/role-menu-item";
import { ChangeRoleEntityType, useChangeRoleBulkDialogContent } from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { AssignableUpdateCompanyMemberRole } from "@custom-types/member-types";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { bulkActionNameSelector, selectedEntitiesSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, updateMany, updateOne } from "@store/table/table-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { formatUserRoleType } from "@utils/data-display";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { Text, UserRoleIcon, MenuItem } from "@flat-ui";
/**
 * Bulk-assignable workspace roles (Group Viewer is always excluded upstream in
 * `AssignableUpdateCompanyMemberRole`). Admin can only be assigned when the
 * workspace can assign managers. "No workspace role" is offered for teams, and
 * also for members when managers can't be assigned (Scan Essential and below),
 * where every member is a viewer that can be safely downgraded.
 */
function getBulkAssignableCompanyRoles(entityType, canAssignManagers) {
  const canAssignNoRole = entityType === "teams" || !canAssignManagers;
  return AssignableUpdateCompanyMemberRole.filter(role => {
    if (role === "COMPANY_EXECUTIVE") {
      return canAssignManagers;
    }
    if (role === "MEMBER") {
      return canAssignNoRole;
    }
    return true;
  });
}

/** Button allowing to change role of members or teams in company */
export function BulkChangeCompanyRole(t0) {
  const $ = _c(48);
  const {
    companyId,
    entityType
  } = t0;
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [newRole, setNewRole] = useState("MEMBER");
  const currentUser = useAppSelector(currentUserSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t1;
  if ($[0] !== entityType) {
    t1 = selectedEntitiesSelector(entityType);
    $[0] = entityType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedEntities = useAppSelector(t1);
  const mode = useBulkActionsMode();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const dispatch = useAppDispatch();
  const t2 = entityType === "members" ? ChangeRoleEntityType.member : ChangeRoleEntityType.team;
  let t3;
  if ($[2] !== newRole || $[3] !== t2) {
    t3 = {
      subjectType: "workspace",
      newRole,
      entityType: t2
    };
    $[2] = newRole;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    bulkDialogContent,
    successDialogContent
  } = useChangeRoleBulkDialogContent(t3);
  const assignableRoles = getBulkAssignableCompanyRoles(entityType, canAssignManagers);
  let t4;
  let t5;
  if ($[5] !== shouldShowSuccessDialog) {
    t4 = () => {
      if (shouldShowSuccessDialog) {
        setShouldShowDialog(false);
      }
    };
    t5 = [shouldShowSuccessDialog];
    $[5] = shouldShowSuccessDialog;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== currentUser || $[9] !== dispatch || $[10] !== selectedEntities) {
    t6 = function onNewRoleClicked(event) {
      dispatch(setBulkActionName("changeRole"));
      const notAllowedChanges = [];
      for (const entity of selectedEntities) {
        if (event.currentTarget.dataset.myValue === "MEMBER" && (entity.role === "COMPANY_MANAGER" || entity.role === "PROJECT_MANAGER")) {
          notAllowedChanges.push({
            id: entity.identity,
            changes: {
              status: "not-allowed",
              message: `You cannot change from ${formatUserRoleType(entity.role)} role to ${formatUserRoleType("MEMBER")}.`
            }
          });
        }
      }
      if (notAllowedChanges.length) {
        dispatch(updateMany(notAllowedChanges));
      }
      if (currentUser) {
        dispatch(updateOne({
          id: currentUser.identity,
          changes: {
            status: "not-allowed",
            message: "You cannot change the role of yourself."
          }
        }));
      }
      setNewRole(event.currentTarget.dataset.myValue);
      setShouldShowDialog(true);
    };
    $[8] = currentUser;
    $[9] = dispatch;
    $[10] = selectedEntities;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onNewRoleClicked = t6;
  let t7;
  if ($[12] !== mode) {
    t7 = getBulkActionButtonConfig(mode, "Change role in workspace");
    $[12] = mode;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const config = t7;
  const T0 = FaroTextMenuButton;
  let t8;
  if ($[14] !== config.shouldShowText) {
    t8 = config.shouldShowText ? <Text variant="buttonM">Change role</Text> : undefined;
    $[14] = config.shouldShowText;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = config.tooltipText;
  let t10;
  if ($[16] !== config.sx) {
    t10 = () => config.sx;
    $[16] = config.sx;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      anchorOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "bottom",
        horizontal: "left"
      }
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = config.rippleColor;
  const t13 = false;
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <UserRoleIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== onNewRoleClicked) {
    t15 = role => <MenuItem data-my-value={role} key={role} value={role} onClick={onNewRoleClicked} sx={getRoleMenuItemSx({
      isTableCell: false
    })}><RoleMenuItem role={role} /></MenuItem>;
    $[20] = onNewRoleClicked;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = assignableRoles.map(t15);
  let t17;
  if ($[22] !== T0 || $[23] !== config.rippleColor || $[24] !== config.tooltipText || $[25] !== t10 || $[26] !== t11 || $[27] !== t14 || $[28] !== t16 || $[29] !== t8) {
    t17 = <T0 buttonTitle={t8} tooltipText={t9} buttonSx={t10} menuPosition={t11} rippleColor={t12} shouldShowEndIcon={t13} startIcon={t14}>{t16}</T0>;
    $[22] = T0;
    $[23] = config.rippleColor;
    $[24] = config.tooltipText;
    $[25] = t10;
    $[26] = t11;
    $[27] = t14;
    $[28] = t16;
    $[29] = t8;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== bulkActionName || $[32] !== shouldShowSuccessDialog || $[33] !== successDialogContent) {
    t18 = bulkActionName === "changeRole" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} />;
    $[31] = bulkActionName;
    $[32] = shouldShowSuccessDialog;
    $[33] = successDialogContent;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== bulkActionName || $[36] !== bulkDialogContent || $[37] !== companyId || $[38] !== dispatch || $[39] !== entityType || $[40] !== newRole || $[41] !== selectedEntities || $[42] !== shouldShowDialog) {
    t19 = bulkActionName === "changeRole" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={() => setShouldShowDialog(false)} trackingEvent={BulkActionEvents.bulkChangeRole} bulkActionCallback={member => dispatch(updateMemberRoleInWorkspace({
      companyId,
      role: newRole,
      identity: member.identity
    })).unwrap()} selectedEntities={selectedEntities} uniqueIdKey="identity" tableDataType={entityType} />;
    $[35] = bulkActionName;
    $[36] = bulkDialogContent;
    $[37] = companyId;
    $[38] = dispatch;
    $[39] = entityType;
    $[40] = newRole;
    $[41] = selectedEntities;
    $[42] = shouldShowDialog;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t17 || $[45] !== t18 || $[46] !== t19) {
    t20 = <>{t17}{t18}{t19}</>;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  return t20;
}